import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { formatHours, formatNOK } from "@/lib/format";
import type { OfferTotalsResult } from "@/lib/offer-edit-calc";

interface Props {
  totals: OfferTotalsResult;
  overheadPct: number;
  discountPct: number;
  discountAmount: number;
  hourlyRate: number;
  /** Mål-timesats fra system_config — sammenlikning i BF-info. */
  targetHourlyRate: number;
  readOnly?: boolean;
  onChange: (patch: { overhead_pct?: number; discount_pct?: number; discount_amount?: number }) => void;
}

/**
 * Lys, diskret footer for tilbud-editoren. Inputs venstre, beregninger
 * høyre. BF + estimert timepris (GEEK per intern time) vises som en
 * sekundær linje under, med interne timer som nevner ved siden av og
 * advarsel når timepris &lt; mål (VIB-555).
 */
export function OfferEditFooter({
  totals,
  overheadPct,
  discountPct,
  discountAmount,
  hourlyRate,
  targetHourlyRate,
  readOnly,
  onChange,
}: Props) {
  const timepresIsLow =
    totals.estHourlyRate !== null && totals.estHourlyRate < targetHourlyRate;

  return (
    <div className="border rounded-md bg-muted/20 px-4 py-2.5 mt-2.5">
      {/* Hovedlinje: inputs + total. Mobile: stack vertikalt slik at
          totals ikke klemmer inputene. */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 md:flex-wrap">
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          Overhead
          <Input
            type="number"
            min={0}
            step={1}
            value={overheadPct}
            disabled={readOnly}
            onChange={(e) => onChange({ overhead_pct: Number(e.target.value) || 0 })}
            className="w-14 h-7 text-right text-xs tabular-nums"
          />
          <span>%</span>
        </label>

        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          Rabatt
          <Input
            type="number"
            min={0}
            step={1}
            value={discountPct}
            disabled={readOnly}
            onChange={(e) => onChange({ discount_pct: Number(e.target.value) || 0 })}
            className="w-14 h-7 text-right text-xs tabular-nums"
          />
          <span>%</span>
        </label>

        <label
          className="inline-flex items-center gap-2 text-xs text-muted-foreground"
          title="Beløp overstyrer prosent når > 0"
        >
          eller
          <Input
            type="number"
            min={0}
            step={500}
            value={discountAmount}
            disabled={readOnly}
            onChange={(e) => onChange({ discount_amount: Number(e.target.value) || 0 })}
            className="w-24 h-7 text-right text-xs tabular-nums"
          />
          <span>kr</span>
        </label>

        <span className="hidden md:block flex-1" />

        <div className="flex flex-col md:flex-row md:items-baseline gap-2 md:gap-4 text-sm md:flex-wrap">
          <span className="text-muted-foreground">
            Sum <span className="text-foreground tabular-nums">{formatNOK(totals.subtotal)}</span>
          </span>
          <span className="text-muted-foreground">
            + <span className="text-foreground tabular-nums">{formatNOK(totals.overhead_amount)}</span> OH
          </span>
          {totals.discount_amount > 0 && (
            <span className="text-destructive tabular-nums">
              − {formatNOK(totals.discount_amount)}
            </span>
          )}
          <span className="text-muted-foreground">
            · Innkjøp <span className="text-foreground tabular-nums">{formatNOK(totals.externalCost)}</span>
          </span>
          <span
            className={cn(
              "md:border-l md:border-border md:pl-4 md:ml-1",
              "pt-2 md:pt-0 border-t md:border-t-0 border-border/40",
              "font-display font-semibold text-lg tabular-nums text-foreground",
            )}
          >
            {formatNOK(totals.total)}
          </span>
        </div>
      </div>

      {/* Sekundærlinje: BF + estimert timepris */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap mt-2 pt-2 border-t border-border/40 text-xs">
        <span className="uppercase tracking-wider text-[10px] font-semibold text-muted-foreground">
          GEEK
        </span>
        <span className="tabular-nums font-medium">{formatNOK(totals.bf)}</span>
        <span className="text-muted-foreground/50">·</span>
        <span className="uppercase tracking-wider text-[10px] font-semibold text-muted-foreground">
          Est. timepris
        </span>
        <span
          className={cn(
            "tabular-nums font-medium",
            timepresIsLow && "text-destructive",
          )}
        >
          {totals.estHourlyRate !== null ? `${formatNOK(totals.estHourlyRate)}/t` : "—"}
        </span>
        <span
          className="text-muted-foreground"
          title="Interne timer i tilbudet (nevneren i est. timepris)"
        >
          ({formatHours(totals.internalHours)} internt)
        </span>
        {timepresIsLow && (
          <span className="text-[10px] text-destructive/80">
            (mål {formatNOK(targetHourlyRate)}/t)
          </span>
        )}
        {hourlyRate !== targetHourlyRate && (
          <>
            <span className="text-muted-foreground/50">·</span>
            <span className="text-muted-foreground">
              Tilbud-timesats {formatNOK(hourlyRate)}/t (overstyrt)
            </span>
          </>
        )}
      </div>
    </div>
  );
}
